'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import useRegisterStore from '@/stores/register';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useRegisterPageFactory from '@/components/RegisterPage/Factory';
import { toast } from '@/components/toast';
import { useCallback, useEffect, useMemo, useTransition, useRef } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { checkPhoneExistence } from '@/app/(auth)/actions';
import BackgroundVideo from '@/components/BackgroundVideo';
import useRudderStackAnalytics from '@/hooks/useRudderAnalytics';
import useIsWebview from '@/hooks/use-is-webview';
import { isAppVersionAtLeast } from '@/lib/utils/platform';
import { PierreRiveIdle } from '@/components/pierre-rive-idle';
import { useSearchParams } from 'next/navigation';
const RegisterWelcomeStep = () => {
  const $ = _c(45);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["nextStep", "handleChangePhoneNumber", "handleSendWhatsAppVerification", "isSendingWhatsAppCode", "setSkippedOtp"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    nextStep,
    handleChangePhoneNumber,
    handleSendWhatsAppVerification,
    isSendingWhatsAppCode,
    setSkippedOtp
  } = useRegisterStore(t0);
  const analytics = useRudderStackAnalytics();
  const [isWebView] = useIsWebview();
  const hasTrackedPageView = useRef(false);
  const searchParams = useSearchParams();
  const phoneParam = searchParams.get("phone");
  const emailParam = searchParams.get("email");
  const hasPrefilledData = !!(phoneParam || emailParam);
  let t1;
  if ($[1] !== searchParams) {
    bb0: {
      const nameParam = searchParams.get("name");
      if (!nameParam) {
        t1 = null;
        break bb0;
      }
      t1 = nameParam.split(" ")[0];
    }
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const firstName = t1;
  const {
    registerForm
  } = useRegisterPageFactory();
  const [isCheckingPhone, startCheckingPhone] = useTransition();
  let t2;
  if ($[3] !== handleSendWhatsAppVerification || $[4] !== nextStep || $[5] !== registerForm || $[6] !== setSkippedOtp) {
    t2 = async () => {
      const phoneNumber = registerForm.watch("phoneNumber");
      const phoneNumberError = registerForm.formState.errors.phoneNumber;
      await registerForm.trigger("phoneNumber");
      if (phoneNumberError || !phoneNumber) {
        toast({
          type: "error",
          description: "Preencha o n\xFAmero de telefone para continuar"
        });
        return;
      }
      const formattedPhone = `55${phoneNumber.replace(/\D/g, "").trim()}`;
      const formData = new FormData();
      formData.append("phoneNumber", formattedPhone);
      startCheckingPhone(async () => {
        const result = await checkPhoneExistence({
          status: "idle",
          exists: false,
          skipOtp: false
        }, formData);
        if (result.skipOtp) {
          setSkippedOtp(true);
          nextStep();
          nextStep();
          return;
        }
        setSkippedOtp(false);
        if (result.status === "invalid_data") {
          toast({
            type: "error",
            description: "N\xFAmero de telefone inv\xE1lido"
          });
          return;
        }
        if (result.status === "failed") {
          toast({
            type: "error",
            description: "Erro ao verificar telefone. Tente novamente."
          });
          return;
        }
        if (result.exists) {
          toast({
            type: "error",
            description: "J\xE1 existe uma conta com esse n\xFAmero de celular"
          });
          return;
        }
        await handleSendWhatsAppVerification(registerForm, nextStep);
      });
    };
    $[3] = handleSendWhatsAppVerification;
    $[4] = nextStep;
    $[5] = registerForm;
    $[6] = setSkippedOtp;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleNext = t2;
  let t3;
  bb1: {
    if (isCheckingPhone) {
      let t4;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <div className="flex items-center gap-2"><Spinner size="lg" spinnerClassName="bg-black md:bg-white md:dark:bg-black" /><span>Verificando...</span></div>;
        $[8] = t4;
      } else {
        t4 = $[8];
      }
      t3 = t4;
      break bb1;
    }
    if (isSendingWhatsAppCode) {
      let t4;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <div className="flex items-center gap-2"><Spinner size="lg" spinnerClassName="bg-black md:bg-white md:dark:bg-black" /><span>Enviando código...</span></div>;
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      t3 = t4;
      break bb1;
    }
    t3 = hasPrefilledData ? "Continuar" : "Come\xE7ar";
  }
  const buttonContent = t3;
  let t4;
  let t5;
  if ($[10] !== analytics || $[11] !== isWebView) {
    t4 = () => {
      if (!analytics || hasTrackedPageView.current) {
        return;
      }
      hasTrackedPageView.current = true;
      analytics.track("Register Step", {
        step: "welcome",
        platform: isWebView ? "app" : "web"
      });
    };
    t5 = [analytics, isWebView];
    $[10] = analytics;
    $[11] = isWebView;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  if ($[14] !== isWebView) {
    t6 = e => {
      if (isWebView && window.ReactNativeWebView && isAppVersionAtLeast("1.1.0")) {
        e.preventDefault();
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: "NAVIGATE",
          path: "/(auth)/login"
        }));
      }
    };
    $[14] = isWebView;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleLoginClick = t6;
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex justify-center md:justify-start mb-2"><div className="border-[0.5px] border-white/20 dark:border-white/20 rounded-3xl bg-transparent"><PierreRiveIdle className="size-[80px]" /></div></div>;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = firstName ? `Boas vindas, ${firstName}!` : "Boas vindas!";
  let t9;
  if ($[17] !== t8) {
    t9 = <h2 className="text-primary text-3xl lg:text-4xl font-bold">{t8}</h2>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="text-muted-foreground text-base">Qual é o seu número de WhatsApp?</span>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t9) {
    t11 = <div className="flex flex-col gap-3 text-center md:text-left">{t7}{t9}{t10}</div>;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== handleChangePhoneNumber) {
    t12 = t13 => {
      const {
        field
      } = t13;
      return <FormItem className="relative"><FormControl><div className="relative flex gap-4"><div className="h-14 select-none cursor-not-allowed px-4 gap-2 py-3 text-lg rounded-xl border-input bg-transparent flex justify-center items-center w-fit border ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors "><Image src="/images/brasil-flag.svg" alt="bandeira do Brasil" width={25} height={25} /><span className="text-muted-foreground">+55</span></div><Input {...field} inputSize="xl" rounded="2xl" variant="outline" floatingLabel={true} label={"N\xFAmero do WhatsApp"} inputMode="numeric" maxLength={15} onChange={e_0 => {
              const formattedValue = handleChangePhoneNumber(e_0);
              field.onChange(formattedValue);
            }} /></div></FormControl></FormItem>;
    };
    $[22] = handleChangePhoneNumber;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== registerForm.control || $[25] !== t12) {
    t13 = <div className="space-y-4"><FormField control={registerForm.control} name="phoneNumber" render={t12} /></div>;
    $[24] = registerForm.control;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t11 || $[28] !== t13) {
    t14 = <div className="flex flex-col gap-8 md:gap-6">{t11}{t13}</div>;
    $[27] = t11;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = isSendingWhatsAppCode || isCheckingPhone;
  let t16;
  if ($[30] !== buttonContent || $[31] !== handleNext || $[32] !== t15) {
    t16 = <Button type="button" rounded="full" className="w-full" onClick={handleNext} size="xl" disabled={t15}>{buttonContent}</Button>;
    $[30] = buttonContent;
    $[31] = handleNext;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== handleLoginClick) {
    t17 = <div className="text-center"><p className="text-primary text-sm">Já tem uma conta?{" "}<Link href="/login" className="underline hover:text-[#a3ff12] transition-colors" onClick={handleLoginClick}>Fazer login</Link></p></div>;
    $[34] = handleLoginClick;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t16 || $[37] !== t17) {
    t18 = <div className="w-full space-y-3">{t16}{t17}</div>;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t14 || $[40] !== t18) {
    t19 = <div className="w-full lg:flex-1 flex flex-col lg:p-16"><div className="flex-1 flex flex-col justify-center px-4 py-10 gap-12 md:gap-10 max-w-md mx-auto w-full">{t14}{t18}</div></div>;
    $[39] = t14;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="hidden lg:block w-[850px] max-w-[850px] m-3 lg:w-[55%] relative"><BackgroundVideo src="/videos/background.webm" className="rounded-3xl" /></div>;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t19) {
    t21 = <div className="min-h-screen w-full flex bg-white dark:bg-black">{t19}{t20}</div>;
    $[43] = t19;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  return t21;
};
export default RegisterWelcomeStep;